import { useEffect } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { getUserFromClerkId } from '@/hooks/useUser';
import { useMyPendingInvitations } from '@/hooks/useInvitation';

// Catchall redirect for the welcome-kit/street-sales scenario: a user signs in
// without an invitationcode in the URL, but a PENDING invitation matching one
// of their verified Clerk emails exists. We route them through the existing
// /invite flow (single invite) or /pending-invitations picker (multiple).
//
// Mounted once in __root.tsx so it runs across both /_auth routes and top-level
// post-auth routes like /subscribe.

// Paths where we never auto-redirect: the existing /invite flow already runs,
// or the user is mid-checkout / mid-auth.
const SKIP_PATHS = [
  '/invite',
  '/pending-invitations',
  '/signing-in',
  '/signin',
  '/signup',
  '/register',
  '/checkout-success',
  '/checkout-cancel',
];

function isSkipPath(pathname: string): boolean {
  return SKIP_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

export default function PendingInviteRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: clerkUser, isLoaded: clerkLoaded } = useClerkUser();
  const { data: appUser, isLoading: userLoading } = getUserFromClerkId(clerkUser?.id ?? '', {
    enabled: !!clerkUser,
  });

  // URL-driven flows always win — do not compete with /signin?invitationcode=
  // or Clerk's __clerk_ticket handoff.
  const hasUrlInvite =
    typeof window !== 'undefined' &&
    (() => {
      try {
        const url = new URL(window.location.href);
        return !!url.searchParams.get('invitationcode') || !!url.searchParams.get('__clerk_ticket');
      } catch {
        return false;
      }
    })();

  // Family admins can't accept invites — the /invite page would just show them
  // an error. Silently skip so signing in is uneventful.
  const childUsers = (appUser as any)?.childUsers as unknown[] | undefined;
  const isFamilyAdmin = Array.isArray(childUsers) && childUsers.length > 0;

  // Per-session, per-user dismissal flag so "Not right now" doesn't loop.
  // Keyed by app user id so two users on a shared device get independent state.
  const sessionFlagKey = appUser?.id ? `gg.pending-invite-handled.${appUser.id}` : null;
  const alreadyHandled =
    typeof window !== 'undefined' && sessionFlagKey
      ? window.sessionStorage.getItem(sessionFlagKey) === '1'
      : false;

  const enabled =
    clerkLoaded &&
    !!clerkUser &&
    !userLoading &&
    !!appUser &&
    !isSkipPath(location.pathname) &&
    !hasUrlInvite &&
    !isFamilyAdmin &&
    !alreadyHandled;

  const { data: pending, isLoading: pendingLoading } = useMyPendingInvitations({ enabled });

  useEffect(() => {
    if (!enabled || pendingLoading || !pending || pending.length === 0) return;

    // Mark handled before navigating so any re-render of this component during
    // the redirect doesn't trigger the effect a second time.
    if (sessionFlagKey && typeof window !== 'undefined') {
      window.sessionStorage.setItem(sessionFlagKey, '1');
    }

    if (pending.length === 1) {
      navigate({
        to: '/invite',
        search: { invitationcode: pending[0].invitationCode, __clerk_ticket: undefined },
      });
    } else {
      navigate({ to: '/pending-invitations' });
    }
  }, [enabled, pendingLoading, pending, navigate, sessionFlagKey]);

  return null;
}
